// Cloning a component copies its properties verbatim, and some of those
// properties address another component by uuid: a table names the data source
// it reads (`data_source_uuid`, or a `datasource://<uuid>/<field>` binding), a
// form field names the input it mirrors (`component://<uuid>/value`). When the
// addressed component is cloned along with the one holding the reference, the
// copy has to address the copy — otherwise the cloned subtree keeps feeding off
// the original and editing one changes the other.
//
// References to components outside the cloned subtree are left untouched: those
// components are shared, not copied.

import { COMPONENT_REFERENCE_PREFIX } from "@/interfaceBuilder/componentReference";
import { DATA_SOURCE_REFERENCE_PREFIX } from "@/interfaceBuilder/dataSourceReference";

const REFERENCE_PREFIXES = [
  COMPONENT_REFERENCE_PREFIX,
  DATA_SOURCE_REFERENCE_PREFIX,
] as const;

/**
 * Rewrites every reference to a cloned component found in `value`, keyed by the
 * uuid of the original. Walks nested objects and arrays, so a reference held
 * inside an object property or a list item (a table column, a button action) is
 * rewritten too. The value is copied, never mutated.
 */
export function remapComponentReferences<TValue>(
  value: TValue,
  clonedUuidsByOriginalUuid: ReadonlyMap<string, string>,
): TValue {
  if (clonedUuidsByOriginalUuid.size === 0) return value;

  return remapValue(value, clonedUuidsByOriginalUuid) as TValue;
}

function remapValue(
  value: unknown,
  clonedUuids: ReadonlyMap<string, string>,
): unknown {
  if (typeof value === "string") return remapReferenceString(value, clonedUuids);
  if (Array.isArray(value)) {
    return value.map((item) => remapValue(item, clonedUuids));
  }
  if (isRecord(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, fieldValue]) => [
        key,
        remapValue(fieldValue, clonedUuids),
      ]),
    );
  }

  return value;
}

function remapReferenceString(
  value: string,
  clonedUuids: ReadonlyMap<string, string>,
): string {
  // Surrounding whitespace is tolerated when a reference is parsed, so it is
  // tolerated here too.
  const trimmed = value.trim();
  // A property that holds the plain uuid of another component, as the inspector
  // writes it for a `component_kind` field.
  const clonedUuid = clonedUuids.get(trimmed);
  if (clonedUuid) return clonedUuid;

  for (const prefix of REFERENCE_PREFIXES) {
    if (!trimmed.startsWith(prefix)) continue;

    const remainder = trimmed.slice(prefix.length);
    const pathIndex = remainder.indexOf("/");
    const uuid = pathIndex === -1 ? remainder : remainder.slice(0, pathIndex);
    const clonedReferenceUuid = clonedUuids.get(uuid);
    if (!clonedReferenceUuid) return value;

    // The path is kept exactly as written: only the uuid names a clone.
    const path = pathIndex === -1 ? "" : remainder.slice(pathIndex);
    return `${prefix}${clonedReferenceUuid}${path}`;
  }

  return value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
